import React, { useEffect, useRef, useState } from 'react';
import { Coins, Search } from 'lucide-react';
import T from 'Components/Helpers/translate';
import {
	PROVIDER_NAMES,
	runLookup,
	STATUS_KEYS,
	withChannel,
	type LookupChannel,
	type LookupResult,
	type LookupValue
} from 'App/shadcn/pages/ProspectingTools/Lookup/lookupApi';
import { sourceOf } from 'App/shadcn/pages/ProspectingTools/Lookup/LookupParts';
import { formatCredits } from 'App/shadcn/pages/AIWorkspace/Admin/workspaceSettingsShared';
import FillExistingContact from './FillExistingContact';

export type FoundContactDetails = { email?: string; phone?: string };

type Run = 'idle' | 'finding' | 'done' | 'failed';

const IDLE: Record<LookupChannel, Run> = { email: 'idle', phoneNumber: 'idle' };

const CHANNELS: Array<{ channel: LookupChannel; find: string; label: string; notFound: string }> = [
	{
		channel: 'email',
		find: 'assistant.embedPersonFindContact',
		label: 'prospectingTools.lookup.channel.email',
		notFound: 'prospectingTools.lookup.emailNotFound'
	},
	{
		channel: 'phoneNumber',
		find: 'prospectingTools.lookup.findPhoneNumber',
		label: 'voice.phoneNumber',
		notFound: 'prospectingTools.lookup.phoneNotFound'
	}
];

const Found: React.FC<{ label: string; found: LookupValue | null }> = ({ label, found }) =>
	found ? (
		<div className="embed-add-fv">
			<span className="embed-add-fv-label">{label}</span>
			<span className="embed-add-fv-value">{`${found.value} · ${sourceOf(found)}`}</span>
		</div>
	) : null;

type Props = {
	linkedinUrl: string;
	/** What was found, for the contact about to be added. Called again with `{}` for a new profile. */
	onFound: (details: FoundContactDetails) => void;
	/** The CRM contact this profile already is, if any: then what was found can be added to it (WEB-13446). */
	existingContactId?: number;
	/** What that contact already has: not offered for lookup, since it would spend credits for nothing (WEB-13771). */
	inCrm?: Record<LookupChannel, boolean>;
	/** After "Add to the contact" saved, so the card can show the contact's new values. */
	onFilled?: () => void;
};

/**
 * "Find email" and "Find phone number" on a LinkedIn profile in the extension's Add person tab
 * (WEB-13374). The same Lookup as supersearch's, so Airscale first under AIRSCALE_INTEGRATION and the
 * customer's waterfall otherwise, charged the same way. Each runs only when the rep asks: the phone
 * costs about 20 times the email (WEB-13365). The profile is already on screen, so both are offered
 * from the start, in either order, and what each finds lands on one card (WEB-13771).
 */
const FindContactDetails: React.FC<Props> = ({ linkedinUrl, onFound, existingContactId, inCrm, onFilled }) => {
	const [result, setResult] = useState<LookupResult | null>(null);
	const [runs, setRuns] = useState(IDLE);
	// The profile on screen: an answer for one the rep has since left is dropped.
	const shown = useRef(linkedinUrl);

	useEffect(() => {
		shown.current = linkedinUrl;
		setResult(null);
		setRuns(IDLE);
		onFound({});
	}, [linkedinUrl, onFound]);

	const find = (channel: LookupChannel) => {
		setRuns(current => ({ ...current, [channel]: 'finding' }));
		runLookup(linkedinUrl, [channel])
			.then(answer => {
				if (shown.current !== linkedinUrl) return;
				setResult(current => (current ? withChannel(current, answer, channel) : answer));
				setRuns(current => ({ ...current, [channel]: 'done' }));
			})
			.catch((err: unknown) => {
				console.error('[EmbedAIChat] contact lookup failed', { channel, err });
				if (shown.current === linkedinUrl) setRuns(current => ({ ...current, [channel]: 'failed' }));
			});
	};

	const person = result?.person ?? null;

	useEffect(() => {
		if (person) onFound({ email: person.email?.value, phone: person.phoneNumber?.value });
	}, [person, onFound]);

	// No one named at all: the lookup's own reason (not found, no provider, out of credits, EU ONLY).
	const message =
		result && !person ? (result.status === 'found' ? STATUS_KEYS.notFound : STATUS_KEYS[result.status]) : null;
	const offered = CHANNELS.filter(({ channel }) => runs[channel] !== 'done' && !inCrm?.[channel]);

	return (
		<div className="embed-add-fields">
			{/* What it cost, at the top of what it found rather than after it (WEB-13771). */}
			{result && result.aiCredits > 0 ? (
				<div className="embed-add-credits-row">
					<span className="embed-add-credits">
						<Coins aria-hidden />
						{T('prospectingTools.lookup.aiCredits', { count: formatCredits(result.aiCredits) })}
					</span>
				</div>
			) : null}
			{CHANNELS.map(({ channel, label, notFound }) =>
				person?.[channel] ? (
					<Found key={channel} label={T(label)} found={person[channel]} />
				) : person && runs[channel] === 'done' ? (
					<span key={channel} className="embed-add-fv-label">
						{T(notFound)}
					</span>
				) : null
			)}
			{message ? <div className="embed-add-fv-label">{T(message)}</div> : null}
			{CHANNELS.some(({ channel }) => runs[channel] === 'failed') ? (
				<div className="embed-add-error">{T('prospectingTools.lookup.status.failed')}</div>
			) : null}
			{offered.length ? (
				<span className="embed-add-uses-credits">
					<Coins aria-hidden />
					{T('prospectingTools.lookup.usesCredits')}
				</span>
			) : null}
			{offered.length ? (
				<div className="embed-add-find">
					{offered.map(({ channel, find: findKey }) => (
						<button
							key={channel}
							type="button"
							className="embed-add-btn"
							disabled={runs[channel] === 'finding'}
							onClick={() => find(channel)}
						>
							<Search />
							{runs[channel] === 'finding' ? T('prospectingTools.lookup.loading') : T(findKey)}
						</button>
					))}
				</div>
			) : null}
			{result?.outsideEuOwnKeys?.length ? (
				<span className="embed-add-fv-label">
					{T('prospectingTools.lookup.outsideEuOwnKeys', {
						providers: result.outsideEuOwnKeys.map(provider => PROVIDER_NAMES[provider]).join(', ')
					})}
				</span>
			) : null}
			{existingContactId && person && (person.email || person.phoneNumber) ? (
				<FillExistingContact
					// Something found after a fill (the other channel) offers the fill again.
					key={`${person.email?.value ?? ''}|${person.phoneNumber?.value ?? ''}`}
					contactId={existingContactId}
					found={{ email: person.email?.value, phone: person.phoneNumber?.value }}
					onFilled={onFilled}
				/>
			) : null}
		</div>
	);
};

export default FindContactDetails;
